import { useQuery } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import { formatRelative } from "@veil/ui/lib/format";
import {
	AlertTriangle,
	Box,
	CheckCircle2,
	Server,
	TrendingDown,
	TrendingUp,
} from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { api, unwrap } from "@/lib/api";

interface ClusterStatsResponse {
	success: boolean;
	stats?: {
		totalHosts: number;
		enabledHosts: number;
		dirty: boolean;
		lastRender: { finishedAt: string | null } | null;
	};
}

interface OperationsResponse {
	success: boolean;
	data: Array<{
		id: string;
		kind: string;
		status: string;
		startedAt: string;
		finishedAt: string | null;
	}>;
	pagination: { total: number };
}

interface ManifestResponse {
	success: boolean;
	manifest?: {
		generatedAt?: string;
		browserMajors?: Record<
			string,
			{ latestBuild?: string; builds?: Record<string, { releaseDate: string }> }
		>;
	};
}

export function SectionCards() {
	const clusterQuery = useQuery({
		queryKey: ["cluster"],
		queryFn: () =>
			unwrap(api.api.cluster.get()) as Promise<ClusterStatsResponse>,
		refetchInterval: 30_000,
	});
	const opsQuery = useQuery({
		queryKey: ["operations", "24h"],
		queryFn: () =>
			unwrap(
				api.api.operations.get({ query: { pageSize: 100 } }),
			) as Promise<OperationsResponse>,
		refetchInterval: 30_000,
	});
	const manifestQuery = useQuery({
		queryKey: ["manifest"],
		queryFn: () => unwrap(api.api.manifest.get()) as Promise<ManifestResponse>,
		refetchInterval: 60_000,
	});

	const stats = clusterQuery.data?.stats;
	const hostsOnline = stats ? `${stats.enabledHosts}/${stats.totalHosts}` : "—";

	const opsLast24h = useMemo(() => {
		const ops = opsQuery.data?.data ?? [];
		const cutoff = Date.now() - 24 * 60 * 60 * 1000;
		const recent = ops.filter(
			(op) => new Date(op.startedAt).getTime() >= cutoff,
		);
		const succeeded = recent.filter((op) => op.status === "succeeded").length;
		const ratio = recent.length === 0 ? null : succeeded / recent.length;
		return { count: recent.length, ratio };
	}, [opsQuery.data]);

	const latestRelease = useMemo(() => {
		const majors = manifestQuery.data?.manifest?.browserMajors;
		if (!majors) return null;
		const sortedMajors = Object.keys(majors)
			.map((m) => Number.parseInt(m, 10))
			.filter((n) => Number.isFinite(n))
			.sort((a, b) => b - a);
		const top = sortedMajors[0];
		if (top == null) return null;
		const entry = majors[String(top)];
		const releaseDate =
			entry?.latestBuild && entry.builds?.[entry.latestBuild]?.releaseDate;
		return {
			major: top,
			version: entry?.latestBuild ?? null,
			releaseDate: releaseDate ?? null,
		};
	}, [manifestQuery.data]);

	return (
		<section className="grid grid-cols-1 gap-4 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
			<KpiCard
				title="Hosts online"
				icon={<Server className="size-4" />}
				value={hostsOnline}
				loading={clusterQuery.isLoading}
				description={
					stats
						? `${stats.enabledHosts} enabled of ${stats.totalHosts} configured`
						: "No cluster yet"
				}
				action={
					stats && stats.totalHosts > 0 ? (
						<Badge
							variant={
								stats.enabledHosts === stats.totalHosts ? "success" : "warning"
							}
						>
							{stats.enabledHosts === stats.totalHosts ? (
								<TrendingUp className="size-3" />
							) : (
								<TrendingDown className="size-3" />
							)}
							{stats.totalHosts === 0
								? "0%"
								: `${Math.round((stats.enabledHosts / stats.totalHosts) * 100)}%`}
						</Badge>
					) : null
				}
			/>
			<KpiCard
				title="Cluster config"
				icon={<CheckCircle2 className="size-4" />}
				value={stats?.dirty ? "Dirty" : "Clean"}
				loading={clusterQuery.isLoading}
				description={
					stats?.lastRender?.finishedAt
						? `Last render ${formatRelative(stats.lastRender.finishedAt)}`
						: "No successful render yet"
				}
				action={
					stats ? (
						<Badge variant={stats.dirty ? "warning" : "success"}>
							{stats.dirty ? (
								<AlertTriangle className="size-3" />
							) : (
								<CheckCircle2 className="size-3" />
							)}
							{stats.dirty ? "needs reload" : "in sync"}
						</Badge>
					) : null
				}
			/>
			<KpiCard
				title="Operations 24h"
				icon={<Box className="size-4" />}
				value={String(opsLast24h.count)}
				loading={opsQuery.isLoading}
				description={
					opsLast24h.ratio === null
						? "No operations in last 24h"
						: `${Math.round(opsLast24h.ratio * 100)}% success`
				}
				action={
					opsLast24h.ratio !== null ? (
						<Badge
							variant={
								opsLast24h.ratio >= 0.9
									? "success"
									: opsLast24h.ratio >= 0.5
										? "warning"
										: "destructive"
							}
						>
							{opsLast24h.ratio >= 0.9 ? (
								<TrendingUp className="size-3" />
							) : (
								<TrendingDown className="size-3" />
							)}
							{Math.round(opsLast24h.ratio * 100)}%
						</Badge>
					) : null
				}
			/>
			<KpiCard
				title="Latest release"
				icon={<Box className="size-4" />}
				value={
					latestRelease?.version
						? `v${latestRelease.major}`
						: latestRelease
							? `v${latestRelease.major}`
							: "—"
				}
				loading={manifestQuery.isLoading}
				description={
					latestRelease?.version
						? `${latestRelease.version} · ${formatRelative(latestRelease.releaseDate)}`
						: "No published manifest"
				}
				action={
					latestRelease?.version ? (
						<Badge variant="secondary" className="font-mono">
							{latestRelease.version}
						</Badge>
					) : null
				}
			/>
		</section>
	);
}

function KpiCard({
	title,
	icon,
	value,
	description,
	loading,
	action,
}: {
	title: string;
	icon: ReactNode;
	value: string;
	description: string;
	loading: boolean;
	action: ReactNode;
}) {
	return (
		<Card>
			<CardHeader>
				<CardDescription className="flex items-center gap-2">
					{icon}
					{title}
				</CardDescription>
				<CardTitle className="font-mono text-3xl font-semibold tabular-nums">
					{loading ? <Skeleton className="h-8 w-24" /> : value}
				</CardTitle>
				{action && <CardAction>{action}</CardAction>}
			</CardHeader>
			<CardContent>
				<CardFooter className="text-sm text-muted-foreground">
					{loading ? <Skeleton className="h-4 w-32" /> : description}
				</CardFooter>
			</CardContent>
		</Card>
	);
}
